import React, { useMemo } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { Avatar, Button, Checkbox, Stack, Typography } from "@mui/material";
import { Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IEmail } from "store/types";

import { useEmailsContext } from "../context/Email-context";
import { Suggestion } from "./Suggestion";

interface Props {
  email: IEmail;
  checked: boolean;
}

export const ListItem: React.FC<Props> = ({ email, checked }) => {
  const { handleOpenPreview, setSuggestionDeal, emailPreviewId, isFullScreen, handleDelete, onSelectEmail, onMove } =
    useEmailsContext();
  const hasAttachments = !!email.attachments.length;

  const isAssociated = Boolean(email.deal_id);

  const formattedDate = Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    day: "numeric",
    month: "short"
  }).format(new Date(email.created_at));

  const emailSender = useMemo(() => {
    if (!email.sender_name.length) return;
    if (email.sender_name.trim().indexOf(" ") !== -1) {
      //there is at least one space, excluding leading and training spaces
      return `${email.sender_name.split(" ")[0][0]}${email.sender_name.split(" ")[1][0]}`;
    } else return `${email.sender_name.split("")[0][0]}`;
  }, [email.sender_name]);

  return (
    <Stack
      onClick={() => handleOpenPreview({ email })}
      justifyContent="space-between"
      direction="column"
      gap="10px"
      sx={{
        ...(emailPreviewId === email.id &&
          isFullScreen && {
            backgroundColor: "#edf1f9"
          }),
        padding: "12px 12px 12px 12px",
        width: "100%",
        borderRadius: "4px",
        borderBottom: "1px solid #DBE8F5",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#edf1f9"
        }
      }}
    >
      <Stack direction="row">
        <Stack sx={{ display: "flex", flexDirection: "row", width: "100%", gap: "6px" }}>
          {!isAssociated && (
            <Checkbox
              sx={{ paddingTop: 0, paddingRight: 0 }}
              edge="start"
              tabIndex={-1}
              disableRipple
              onClick={(e) => e.stopPropagation()}
              checked={checked}
              onChange={() => onSelectEmail(email)}
            />
          )}
          <Avatar
            sx={{ backgroundColor: "#3a5b9f", width: 30, height: 30, fontSize: "10px" }}
            alt={email.sender_name}
            src={"/static/images/avatar/11.jpg"}
          >
            {emailSender}
          </Avatar>
          <Stack>
            <MaterialTooltip title={email.sender_name}>
              <Typography
                sx={{ margin: 0, maxWidth: "220px" }}
                variant="body2"
                color="primary"
                fontWeight={700}
                lineHeight={1}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {email.sender_name}
              </Typography>
            </MaterialTooltip>
            <MaterialTooltip title={email.email_subject}>
              <Typography
                sx={{ margin: 0, maxWidth: "220px" }}
                variant="body2"
                color="secondary"
                fontSize="13px"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {email.email_subject}
              </Typography>
            </MaterialTooltip>
          </Stack>
        </Stack>
        <Stack alignItems="flex-end">
          <Typography sx={{ whiteSpace: "nowrap", fontSize: "14px", color: "#496682" }}>{formattedDate}</Typography>
          {hasAttachments && <AttachFileIcon sx={{ transform: "rotate(45deg)", fontSize: 20 }} />}
        </Stack>
      </Stack>
      <Stack alignItems="center" justifyContent="space-between" direction="row">
        <Stack flexDirection="column">{<Suggestion email={email} setSuggestionDeal={setSuggestionDeal} />}</Stack>
        <Stack direction="row" gap="12px" alignItems="center">
          {!isAssociated && (
            <Button
              disabled={!email.suggestions?.[0]?.deal}
              sx={{ padding: "0 12px", minWidth: 0 }}
              variant="contained"
              color="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (isFullScreen) {
                  email.suggestions?.[0]?.deal && onMove({ email, deal: email.suggestions?.[0]?.deal });
                  return;
                }
                handleOpenPreview({ email });
              }}
              startIcon={<ForwardToInboxIcon />}
            >
              Associate
            </Button>
          )}
          <Menu
            options={[
              {
                title: "Re-Assign deal",
                icon: <ForwardToInboxIcon />,
                onClick: () => handleOpenPreview({ email, isReAssign: true }),
                hidden: Boolean(!email.deal_id)
              },
              {
                title: "Delete",
                icon: <DeleteOutlineOutlinedIcon />,
                onClick: () => handleDelete(email.id)
              }
            ]}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
